import { GetTimeseriesRequest } from "../types/timeseries";
import { useQuery } from "react-query";
import useQueryState from "./useQueryState";
import { getHybridAnomalyTimeseriesByGeographicalUnitService } from "../services/timeseries";
import Cookies from "js-cookie";
import { useAuthContext } from "./useAuthContext";
import { useUIStore } from "../store/ui";
import { buildIndicatorName } from "../helpers/indicatorName";

export type UseTimeseries = GetTimeseriesRequest;

const accessToken = () => Cookies.get("accessToken");

const useHybridIndexTimeseries = ({
  isEnabled,
  season,
  dataunit,
}: {
  isEnabled: boolean;
  season?: string;
  dataunit?: string;
}) => {
  const isDetrended = useUIStore((s) => s.isDetrended);
  const isLandCoverMasked = useUIStore((s) => s.isLandCoverMasked);
  const { parsedToken } = useAuthContext();
  const { email: userEmail } = parsedToken || {};
  const {
    queryState: { country, geographicalUnit, aggregationLevel, layers },
    queryState,
  } = useQueryState();
  const hybridIndexLayer = layers.find(
    (layer) => layer.displayName === "Hybrid Index" && layer.season === season,
  );
  const isSystemType =
    aggregationLevel === "0" ||
    aggregationLevel === "1" ||
    aggregationLevel === "2";
  const getHybridIndexTimeseries = useQuery(
    [
      "timeseries",
      country,
      geographicalUnit,
      aggregationLevel,
      hybridIndexLayer,
      isSystemType ? "system" : "user",
      accessToken() ? userEmail : "",
      isDetrended,
      isLandCoverMasked,
    ],
    async () => {
      const hybridIndicatorsJson: any = [];
      if (hybridIndexLayer) {
        hybridIndexLayer?.parameters?.forEach((parameter) => {
          if (parameter.name === "Soil Moisture")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
          else if (parameter.name === "Precipitation")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
          else if (parameter.name === "Vegetation")
            hybridIndicatorsJson.push({
              indicator: buildIndicatorName(parameter.name, {
                isLandCoverMasked,
                isDetrended,
              }),
              weight: Number(parameter.value),
            });
        });
      }
      const res: any =
        await getHybridAnomalyTimeseriesByGeographicalUnitService({
          country,
          locationId: geographicalUnit,
          adminLevel: aggregationLevel,
          months: hybridIndexLayer?.months ?? [],
          dekads: hybridIndexLayer?.dekads ?? [],
          hybridIndicatorsJson: JSON.stringify(hybridIndicatorsJson),
          type: isSystemType ? "system" : "user",
        });

      return {
        ...res,
        data: { ...res.data, season, dataunit },
      };
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: Boolean(
        country &&
        geographicalUnit &&
        aggregationLevel &&
        hybridIndexLayer &&
        hybridIndexLayer?.months.every((m) => !!m) &&
        hybridIndexLayer?.dekads.every((d) => !!d) &&
        hybridIndexLayer?.parameters &&
        Number(hybridIndexLayer?.parameters[0].value) +
          Number(hybridIndexLayer?.parameters[1].value) ===
          100 &&
        season &&
        queryState.season.includes(season) &&
        isEnabled,
      ),
    },
  );

  return {
    getHybridIndexTimeseries,
  };
};

export default useHybridIndexTimeseries;
